// Types for optional overrides
type ValidationUIElements = {
  wrapper: HTMLElement | null;
  errorEl?: HTMLElement | null;
  softErrorEl?: HTMLElement | null;
  errorNumberEl?: HTMLElement | null;
};

type GetUIElementsFn = (input: HTMLInputElement) => ValidationUIElements;

type ValidateInputOptions = {
  getUIElements?: GetUIElementsFn;
};

// Default UI structure lookup
const defaultGetUIElements: GetUIElementsFn = (input) => {
  const wrapper = input.closest('.event-planner_hero_input-wrapper') as HTMLElement | null;
  return {
    wrapper,
    errorEl: wrapper?.querySelector('.event-planner_error'),
    softErrorEl: wrapper?.querySelector('.event-planner_soft-error'),
    errorNumberEl: wrapper?.querySelector('[data-el="error_number"]'),
  };
};

// Utility: pattern matching
function isPatternValid(value: string, pattern: string | null): boolean {
  if (!pattern) return true;
  try {
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(value);
  } catch {
    return true; // fail-safe if pattern is invalid
  }
}

// Utility: soft error condition
function shouldShowSoftError(value: number, min: number | null): boolean {
  return min !== null && value > 0 && value < min;
}

// Core validation logic
export function validateInput(
  input: HTMLInputElement,
  options: ValidateInputOptions = {}
): boolean {
  const getUI = options.getUIElements || defaultGetUIElements;
  const { wrapper, errorEl, softErrorEl, errorNumberEl } = getUI(input);

  if (!wrapper) return true;

  // Reset UI
  wrapper.classList.remove('is-error');
  if (errorEl) errorEl.textContent = '';
  if (softErrorEl) softErrorEl.style.display = 'none';

  const inputValue = input.value;
  const pattern = input.getAttribute('pattern');
  const minAttr = input.getAttribute('min');
  const min = minAttr !== null ? Number(minAttr) : null;
  const numericValue = Number(inputValue);

  let isValid = input.checkValidity() && isPatternValid(inputValue, pattern);

  // Soft error
  const showSoftError = shouldShowSoftError(numericValue, min);
  if (showSoftError && softErrorEl && errorNumberEl) {
    softErrorEl.style.display = 'flex';
    errorNumberEl.textContent = numericValue === 1 ? '1 room' : `${numericValue} rooms`;
    isValid = false;
  }

  // Final error message
  if (!isValid && errorEl) {
    wrapper.classList.add('is-error');
    errorEl.textContent = input.getAttribute('title') || 'Invalid input';
  }

  return isValid;
}

/**
 * Validates the email in the backend and returns the error message if there is one
 
 * @param apiDomain - The API domain depending on the environment
 * @param email - The email to validate
 * @returns The email error message or an empty string if there are no errors
 */
export async function getEmailErrors(apiDomain: string, email: string): Promise<string> {
  try {
    const response = await fetch(`https://${apiDomain}/signups/validate_email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        formUrl: window.location.href,
        mqlReason: 'Sign Up Form',
        leadSource: 'MKT - HE Website',
      }),
    });

    if (response.status === 200) {
      return '';
    } else {
      const data = await response.json();
      return data?.errors?.[0] || 'Something went wrong, please try again.';
    }
  } catch (error) {
    console.error('Email validation error:', error);
    return 'Something went wrong, please try again.';
  }
}

// Live validation attachment
export function attachLiveValidation(input: HTMLInputElement, options?: ValidateInputOptions) {
  if (input.dataset.liveValidationAttached) return;
  input.dataset.liveValidationAttached = 'true';

  const handler = () => validateInput(input, options);
  input.addEventListener('change', handler);
  input.addEventListener('input', handler);
  input.addEventListener('blur', handler);
}
